<template>
  <div class="catalog-view" :class="{ 'sidebar-collapsed': leftSidebarCollapsed }">
    <div class="left-sidebar">
      <div class="toggle-button d-flex justify-content-center align-items-center" @click="leftSidebarCollapsed = !leftSidebarCollapsed">
        <i class="fas fa-caret-right" v-if="leftSidebarCollapsed"></i>
        <i class="fas fa-caret-left" v-else></i>
      </div>
      <div class="catalog-name">
        <i class="fad fa-address-book"></i>
        <span>Dominios</span>
      </div>

      <!-- FILTERS AND VIEWS -->
      <div class="filters" v-if="currentAction === crudActions.READ">
        <div class="filter-items">
          <h3>Filtros de búsqueda</h3>

          <div class="filter-item">
            <label>Nombre, dueño o código de cliente</label>
            <b-form-input class="shadow-none" placeholder="Buscar..."
              v-model.trim="filters.search"
              @keyup.enter="filterItems">
            </b-form-input>
          </div>

          <div class="filter-item">
            <label>Fecha de alta de relación Cliente - Dominio</label>
            <DateSelector
              format="DD/MM/YYYY"
              :editable="false"
              :clearable="true"
              v-model="filters.creation"
              placeholder="dd/mm/aaaa"/>
          </div>

          <div class="filter-item">
            <label>Categoría de cliente</label>
            <b-form-checkbox-group v-model="filters.domainType" stacked>
              <b-form-checkbox size="lg" value="ecosystem">Ecosistema</b-form-checkbox>
              <b-form-checkbox size="lg" value="individual">Cliente individual</b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </div>

        <b-row class="main-actions">
          <b-overlay
            class="main-actions__overlay"
            spinner-type="grow"
            spinner-small
            :show="loadingData">
            <b-col cols="6">
              <b-button block size="sm" class="shadow-sm" @click="cleanFilter" :disabled="loadingData">Limpiar</b-button>
            </b-col>
            <b-col cols="6">
              <b-button variant="primary" block size="sm" class="shadow-sm" @click="filterItems" :disabled="loadingData">Buscar</b-button>
            </b-col>
          </b-overlay>
        </b-row>
      </div>

      <div class="short-list" v-if="currentAction !== crudActions.READ">
        <h5>Dominios registrados</h5>
        <b-list-group>
          <b-list-group-item
            class="text-truncate"
            v-for="item in filteredItems"
            :title="item.clientCode + ' - ' + item.name"
            :key="item.id"
            :active="selectedItem == item"
            @click="selectItem(item)">
            {{ item.name }}
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <div class="right-side">
      <div class="grid">
        <!-- TOP BUTTONS BAR -->
        <div class="top-tool-bar">
          <div class="action d-none d-lg-block" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i> Detalle</div>
          <div class="action d-none d-lg-block" @click="updateItem" v-if="updatePermission" :class="{ disabled: !selectedItem.id }"><i class="fal fa-file-edit"></i> Modificar</div>
          <div v-if="openClosePermission" class="action d-none d-lg-block" @click="showOpenCloseDomainModal" :class="{ disabled: !selectedItem.id }"><i class="fal" :class="[selectedItem.closed ? 'fa-lock-open-alt' : 'fa-lock-alt']"></i> {{ openCloseLabel }}</div>
          <div class="action d-none d-lg-block" @click="showWorkspacesAdmin" :class="{ disabled: !selectedItem.id }"><i class="fal fa-folder-tree"></i> Clientes</div>
          <div class="action d-none d-lg-block" title="Descargar registros" @click="printCsv"><i class="fal fa-file-spreadsheet"></i> Exportar</div>
          <div class="action d-none d-lg-block" title="Refrescar tabla para obtener las últimas actualizaciones" @click="searchData"><i class="fal fa-sync"></i> Refrescar</div>
          <ImportFile v-if="importDomains" :actionName="'Importar Dominios'" :entityName="'Dominios'" :fields="importDomainFields" :entityFields="domainDetails" :prepareEntity="prepareDomain" :saveEntity="saveDomain" :updateEntity="updatedDomain" @reload-data="searchData"/>

          <div class="actions-collapsed d-lg-none">
            <b-dropdown class="shadow-sm" text="Acciones">
              <b-dropdown-item @click="queryItem" v-if="selectedItem.id">Detalle</b-dropdown-item>
              <b-dropdown-item @click="updateItem" v-if="updatePermission && selectedItem.id">Modificar</b-dropdown-item>
              <b-dropdown-item v-if="openClosePermission && selectedItem.id" @click="showOpenCloseDomainModal">{{ openCloseLabel }}</b-dropdown-item>
              <b-dropdown-item @click="showWorkspacesAdmin" v-if="selectedItem.id">Clientes</b-dropdown-item>
              <b-dropdown-item @click="printCsv">Exportar</b-dropdown-item>
              <b-dropdown-item @click="searchData">Refrescar</b-dropdown-item>
            </b-dropdown>
          </div>

          <TopToolBarPaginator :key="paginatorKey" :totalItems="filteredItems.length" :pageSize="PAGE_SIZE_VISIBLE_ITEMS" @pageChange="onPageChange" v-b-tooltip.hover.left="'Total de registros: ' + filteredItems.length"/>
        </div>

        <!-- DATA TABLE -->
        <div class="table-container">
          <table class="data-table table table-hover">
            <thead>
              <tr>
                <template v-for="(col, index) in columns">
                  <th :key="index">
                    <div>
                      {{ col.name }}
                    </div>

                    <div v-if="col.sort" class="sort" @click="sortItems(col)">
                      <i class="fad fa-sort"></i>
                    </div>
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredItems" :key="item.id" @click="selectItem(item)" @dblclick="selectItemAndShowDetail(item)" :class="{ 'selected': isItemSelected(item) }" v-show="isRowVisibleInPage(index)">
                <template v-for="(col, index) in columns">
                  <template v-if="index === 0">
                    <th :key="index" style="max-width: 225px;" class="text-truncate" :title="item[col.key]">
                      {{ item[col.key] }}
                    </th>
                  </template>
                  <template v-else>
                    <td :key="index">
                      <div v-if="col.key === 'closed'" :title="item[col.key] && item[col.key].length > 40 ? item[col.key] : ''">
                        <span class="badge badge-pill" :class="[ item[col.key] ? 'badge-danger' : 'badge-info']">
                          {{ item.getDomainStatusLabel() }}
                        </span>
                      </div>
                      <div v-else :title="item[col.key] && item[col.key].length > 40 ? item[col.key] : ''">{{ item[col.key] }}</div>
                    </td>
                  </template>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- CONSULTA DE DETALLE -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.READ_DETAIL">
          <AdminDomainForm :id="selectedItem.id" :key="selectedItem.id" :readOnly="true" :editButton="updatePermission" :title="selectedItem.name" :showOpenCloseButton="openClosePermission" @close="onCancelForm" @edit="updateItem" @open-close-domain="showOpenCloseDomainModal" />
        </div>
      </transition>

      <!-- NUEVO REGISTRO -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.CREATE">
          <AdminDomainForm :id="selectedItem.id" :key="selectedItem.id" title="Nuevo dominio" @close="currentAction = crudActions.READ" @saved="onItemSaved" />
        </div>
      </transition>

      <!-- ACTUALIZACIÓN -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.UPDATE">
          <AdminDomainForm :id="selectedItem.id" :key="selectedItem.id" :title="'Modificar dominio ' + selectedItem.name" @close="currentAction = crudActions.READ" @saved="onItemSaved" :workspaces="domainWorkspaces" :showOpenCloseButton="openClosePermission" @open-close-domain="showOpenCloseDomainModal" />
        </div>
      </transition>

      <!-- WORKSPACES -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.WORKSPACES">
          <AdminWorkspacesFromDomains :id="selectedItem.id" :key="selectedItem.id" :domain="selectedItem" :title="'Razones sociales de ' + selectedItem.name" :clients="clients" @close="currentAction = crudActions.READ" @saved="onItemSaved" @workspacesLoaded="onItemWorkspacesLoaded" />
        </div>
      </transition>

      <b-modal
        id="open-close-domain-modal"
        header-bg-variant="danger"
        header-text-variant="light"
        @hidden="openCloseDomainConfirmation = false"
      >
        <template v-slot:modal-title>
          <i class="fa fa-exclamation-triangle"></i> {{ openCloseLabel }} dominio: {{ selectedItem.name }}
        </template>
        <p v-if="selectedItem.closed">
          Al abrir un dominio, los usuarios podrán iniciar sesión normalmente.
        </p>
        <p v-else>
          Al cerrar un dominio, las licencias de los usuarios serán desactivadas y no podrán iniciar sesión hasta que el dominio sea abierto nuevamente.
        </p>
        <b-form-checkbox v-model="openCloseDomainConfirmation">Entendido, deseo continuar con {{ selectedItem.closed ? 'la apertura' : 'el cierre' }} del dominio</b-form-checkbox>
        <template v-slot:modal-footer>
          <b-button variant="secondary" :disabled="openCloseDomainLoading" @click="hideOpenCloseDomainModal">Cancelar</b-button>
          <b-button class="w-25" variant="danger" :disabled="!openCloseDomainConfirmation || openCloseDomainLoading" @click="handleOpenCloseDomain">
            <div v-if="openCloseDomainLoading" class="spinner-border text-light spinner-border-sm" role="status">
              <span class="sr-only">Procesando...</span>
            </div>
            <span v-else>{{ selectedItem.closed ? 'Abrir' : 'Cerrar' }}</span>
          </b-button>
        </template>
      </b-modal>

      <!-- LOADING MASK -->
      <div class="loading-mask" v-show="loadingData">
        <i class="far fa-spinner-third fa-spin"></i>
      </div>
    </div>

    <a class="hidden-csv-download-link" ref="csvDownload"></a>
    <div class="on-domain-saved feedback-domain success toast"
      v-if="onSaveDomainFeedback"
      :class="{show:onSaveDomainFeedback}"
      role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-header">
        <strong class="mr-auto">
          Dominio <em>modificado</em> con éxito.
        </strong>
        <button type="button" class="ml-2 mb-1 close"
          @click="hideFeedback"
          data-dismiss="toast" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="toast-body"> Los datos ingresados se han salvado con éxito</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import AdminDomainForm from '@/components/admin/AdminDomainForm.vue'
import AdminWorkspacesFromDomains from '@/components/admin/AdminWorkspacesFromDomains.vue'
import ImportFile from '@/components/generic/ImportFile.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn, DomainCatalogFilters, Domain, DomainCatalogGridItem, Client, Workspace } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames, RoleNames } from '@/types/Auth'
import moment from 'moment'
import reportsService from '@/services/ReportsService'
import { ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminDomainForm,
    AdminWorkspacesFromDomains,
    ImportFile,
    DateSelector,
    TopToolBarPaginator
  }
})
export default class AdminDomains extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  clients: any[] = []
  workspaces: any = {}
  domainWorkspaces: any[] = []
  // Grid columns definition
  columns: CatalogGridItemColumn[] = []
  items: DomainCatalogGridItem[] = []
  filteredItems: DomainCatalogGridItem[] = []
  filters = new DomainCatalogFilters()
  selectedItem = new DomainCatalogGridItem()
  importDomainFields = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nombre' }
  ]
  openCloseDomainConfirmation: boolean = false
  openCloseDomainLoading: boolean = false

  beforeMount () {
    this.columns = [
      CatalogGridItemColumn.def('name', 'Nombre', true),
      CatalogGridItemColumn.def('id', 'ID', true),
      CatalogGridItemColumn.def('status', 'Estado (Workspace dueño)', true),
      CatalogGridItemColumn.def('ownerDomain', 'Dueño del Dominio')
    ]
    if (this.isFinancesRole) {
      this.columns = this.columns.concat([
        CatalogGridItemColumn.def('domainTypeName', 'Categoría de cliente'),
        CatalogGridItemColumn.def('productLine', 'Línea de producto'),
        CatalogGridItemColumn.def('businessSegment', 'Segmento de negocio'),
        CatalogGridItemColumn.def('billingCode', 'Código de facturación')
      ])
    } else if (this.isOperationRole) {
      this.columns = this.columns.concat([
        CatalogGridItemColumn.def('securityLevelName', 'Nivel de seguridad', true),
        CatalogGridItemColumn.def('webBMFMinimumRevision', 'Versión de webbmf mínima'),
        CatalogGridItemColumn.def('broadcast', 'Broadcast'),
        CatalogGridItemColumn.def('usePredictiveDialerOpt', 'Uses predictive dialer options'),
        CatalogGridItemColumn.def('limitLicenses', 'Limite de licencias'),
        CatalogGridItemColumn.def('mapProvider', 'Proveedor de mapas'),
        CatalogGridItemColumn.def('fullTextSearchType', 'Proveedor de búsqueda avanzada'),
        CatalogGridItemColumn.def('trackingMode', 'Tracking mode'),
        CatalogGridItemColumn.def('rateLimitTime', 'Rate limit time'),
        CatalogGridItemColumn.def('rateLimitWS', 'Rate limit WS')
      ])
    }
    this.columns = this.columns.concat([
      CatalogGridItemColumn.def('closed', 'Estado'),
      CatalogGridItemColumn.def('firebaseUUID', 'UUID de Firebase'),
      CatalogGridItemColumn.def('creationDateString', 'Fecha de creación'),
      CatalogGridItemColumn.def('updateDateString', 'Fecha de actualización')
    ])
  }

  onSaveDomainFeedback: boolean = false

  created () {
    this.searchData()
    this.loadClientsPage(new PageRequest(this.PAGE_SIZE))
  }

  @Watch('selectedItem')
  changeDomainWorkspaces (): void {
    this.domainWorkspaces = this.workspaces[this.selectedItem.id]
  }

  /* User permissions */

  /**
   * Validates if the user has a specific role
   *
   * @param role to check
   */
  isUserRole (role: string) {
    return userInfoStore.userInfo.role.name === role
  }

  /** @returns if the user has financesControl or financesAdmin role */
  get isFinancesRole () {
    return this.isUserRole(RoleNames.FINANCES_ADMIN) || this.isUserRole(RoleNames.FINANCES_CONTROL)
  }

  /** @return if the user has operationsAdmin role */
  get isOperationRole () {
    return this.isUserRole(RoleNames.OPERATIONS_ADMIN)
  }

  get createPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_CREATE)
  }

  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_UPDATE)
  }

  get openClosePermission () {
    return userInfoStore.inRootDomain() &&
      userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_OPEN_CLOSE)
  }

  get openCloseLabel () {
    if (this.selectedItem.id === undefined) return 'Abrir/Cerrar'
    if (this.selectedItem.closed) {
      return 'Abrir'
    } else {
      return 'Cerrar'
    }
  }

  get workspacesEditPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_UPDATE)
  }

  /** Import clients */
  get importDomains () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_IMPORT)
  }

  /* Load data */

  searchData () {
    if (this.loadingData) return
    this.items = []
    this.loadingData = true
    this.cleanFilter()
    this.loadPage(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
  }

  async loadPage (pageRequest: PageRequest<DomainCatalogFilters>) {
    pageRequest.sort = 'name'
    const response = await catalogsService.getDomains(pageRequest) as any
    this.items.push(...response.results.map((it: any) =>
      this.fillGridItem(it, new DomainCatalogGridItem())
    ))

    if (this.items.length < response.total) {
      pageRequest.page++
      await this.loadPage(pageRequest)
    } else {
      this.loadWorkspacesPage()
      this.filteredItems = this.items
    }
  }

  async loadClientsPage (pageRequest: PageRequest<any>) {
    const response = await catalogsService.getClients(pageRequest) as any
    this.clients.push(...response.results.map((it: Client) => {
      return { id: it.id, code: it.clientCode, name: it.name, status: it.status }
    }))

    if (this.clients.length < response.total) {
      pageRequest.page++
      await this.loadClientsPage(pageRequest)
    } else {
      this.loadingData = false
    }
  }

  async loadWorkspacesPage () {
    this.workspaces = {}
    const allWorkspaces = await catalogsService.getWorkspacesAll()
    this.items.forEach((domain) => {
      const allWorkspacesForDomain = allWorkspaces
        .filter((workspace) => workspace.domain.id === domain.id)
        .map((workspace) => ({
          clientName: workspace.client.name,
          clientCode: workspace.client.clientCode,
          owner: workspace.owner,
          status: workspace.status,
          creationDate: moment(workspace.creationDate).format('DD/MM/YYYY')
        }))
      this.workspaces[domain.id] = allWorkspacesForDomain
      // Get owner workspace info
      const ownerWorkspace = allWorkspacesForDomain.find((workspace) => workspace.owner)
      if (ownerWorkspace) {
        domain.status = Workspace.getStatusDescription(ownerWorkspace.status)
        domain.ownerDomain = `${ownerWorkspace.clientCode} ${ownerWorkspace.clientName} (Desde el ${ownerWorkspace.creationDate})`
      }
    })
  }

  /* Grid and action events */

  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.WORKSPACES:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.updatePermission ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  selectItemAndShowDetail (item: any) {
    this.selectItem(item)
    this.queryItem()
  }

  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  cleanItemSelected () {
    this.selectedItem = new DomainCatalogGridItem()
  }

  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  newItem () {
    this.selectedItem = new DomainCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  showWorkspacesAdmin () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.WORKSPACES
    }
  }

  fillGridItem (origin: Domain, target: DomainCatalogGridItem) {
    Object.assign(target, origin)
    target.creationDateString = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.updateDateString = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    target.limitLicenses = (origin.limitLicenses ? 'Sí' : 'No') as any
    target.usePredictiveDialerOpt = (origin.usePredictiveDialerOpt ? 'Sí' : 'No') as any
    target.domainTypeName = Domain.getDomainTypeDescription(origin.domainType)
    target.fullTextSearchType = Domain.getDomainFullTextSearchTypeDescription(origin.fullTextSearchType)
    target.securityLevelName = Domain.getDomainSecurityLevelDescription(origin.securityLevel)
    target.closed = origin.closed
    return target
  }

  /* Filter and sorting data */

  cleanFilter () {
    this.resetPaginator()
    this.filters = new DomainCatalogFilters()
    this.filteredItems = this.items
  }

  filterItems () {
    const regexSearch = new RegExp(this.filters.search, 'i')
    this.resetPaginator()
    let creationDateString = this.filters.creation ? moment(this.filters.creation).format('DD/MM/YYYY') : ''
    this.filteredItems = this.items.filter((it: DomainCatalogGridItem, index: number) =>
      (!this.filters.search || regexSearch.test(it.name) || regexSearch.test(it.ownerDomain)) &&
      (!this.filters.domainType.length || this.filters.domainType.includes(it.domainType)) &&
      (!creationDateString || (it.ownerDomain && it.ownerDomain.indexOf(creationDateString) > -1))
    )
    this.cleanItemSelected()
  }

  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  onItemSaved (item: Domain) {
    let isNewItem = false
    if (!this.selectedItem.id) {
      isNewItem = true
      this.selectedItem = new DomainCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
    this.showOnSaveFeedback()

    if (isNewItem && this.workspacesEditPermission) {
      setTimeout(() => {
        this.currentAction = CrudActions.WORKSPACES
      }, 700)
    }
  }

  showOnSaveFeedback () {
    this.onSaveDomainFeedback = true
    setTimeout(() => {
      this.hideFeedback()
    }, 4500)
  }

  onItemWorkspacesLoaded (ws: Workspace[]) {
    if (ws && ws.length) {
      let clientsForDomainFormatted: string[] = []
      ws.forEach((element: Workspace) => {
        clientsForDomainFormatted.push(`${element.client.clientCode} ${element.client.name} (Desde el ${moment(element.creationDate).format('DD/MM/YYYY')})`)
        if (element.owner) {
          Vue.set(this.selectedItem, 'status', Workspace.getStatusDescription(element.status))
        }
      })
      Vue.set(this.selectedItem, 'workspacesInfo', clientsForDomainFormatted.join(', '))
    }
  }

  /** Import file */

  /**
   * Creates a domain instance based on a object with domain values
   * @param domain - a object with domain values to use
   * @returns newClient - a domain instance from Catalogs format
   */
  async prepareDomain (domain: any) {
    try {
      let newDomain = Domain.getIntanceForEdit()
      newDomain.name = domain.hasOwnProperty('name') ? domain['name'] : ''
      newDomain.securityLevel = domain.hasOwnProperty('securityLevel') ? domain['securityLevel'] : ''
      newDomain.minimumRevision = domain.hasOwnProperty('minimumRevision') ? domain['minimumRevision'] : null
      newDomain.webBMFMinimumRevision = domain.hasOwnProperty('webBMFMinimumRevision') ? domain['webBMFMinimumRevision'] : null
      newDomain.broadcast = domain['broadcast']
      newDomain.usePredictiveDialerOpt = domain.hasOwnProperty('usePredictiveDialerOpt') ? domain['usePredictiveDialerOpt'] : null
      newDomain.limitLicenses = domain.hasOwnProperty('limitLicenses') ? domain['limitLicenses'] : null
      newDomain.mapProvider = domain.hasOwnProperty('mapProvider') ? domain['mapProvider'] : null
      newDomain.fullTextSearchType = domain.hasOwnProperty('fullTextSearchType') ? domain['fullTextSearchType'] : null
      newDomain.rateLimitTime = domain.hasOwnProperty('rateLimitTime') ? domain['rateLimitTime'] : null
      newDomain.rateLimitWS = domain.hasOwnProperty('rateLimitWS') ? domain['rateLimitWS'] : null
      newDomain.trackingMode = domain.hasOwnProperty('trackingMode') ? domain['trackingMode'] : null
      newDomain.productLine = domain.hasOwnProperty('productLine') ? domain['productLine'] : null
      newDomain.businessSegment = domain.hasOwnProperty('businessSegment') ? domain['businessSegment'] : null
      newDomain.domainType = domain.hasOwnProperty('domainType') ? domain['domainType'] : null
      newDomain.billingCode = domain.hasOwnProperty('billingCode') ? domain['billingCode'] : null
      if (newDomain.validateRequiredValues().length) {
        throw ErrorImport.getErrorRequiredValues(ImportationErrorAdvices.no_required_domain_values)
      } else {
        let formatErrors = newDomain.validateFormatValues()
        if (formatErrors.length) {
          throw ErrorImport.getErrorFormatValues(
            formatErrors,
            domain.name
          )
        }
      }
      return newDomain
    } catch (error) {
      LogService.error('Error durante la preparación de un dominio.')
      throw error
    }
  }

  /**
   * Gets the domain by id from 'domain' id,
   * and update its values based on same domain parameter
   * @param domain - values to update the domain with id 'domain.id'
   * @returns a domain object updated
   */
  async updatedDomain (domain: any) {
    try {
      let domainEntity = new Domain()
      let currentDomain: Domain = await catalogsService.getDomainById(domain.id)
        .catch((error) => {
          LogService.error(`Error en la consulta del dominio: ${ToString(error.response ?? error)}`)
          throw new ErrorImport(
            ImportErrorCode.NO_RELATED_ENTITY,
            ErrorTitles.domain_not_found,
            `El dominio con el ID: ${domain.id} no se ha podido cargar.`,
            ImportationErrorAdvices.entity_not_found
          )
        })
      Object.assign(domainEntity, currentDomain, domain)
      let missingRequiredValues = domainEntity.validateRequiredValues()
      if (missingRequiredValues.length) {
        throw ErrorImport.getErrorRequiredValues([
          `Lo siguientes valores son necesarios para poder actualizar: ${missingRequiredValues},
          verifica que la columna esta presente y/o que cuenta con un valor. Dominio: ${domainEntity.id} . ${domainEntity.name}`
        ])
      } else {
        let errorFormatValues = domainEntity.validateFormatValues()
        if (errorFormatValues.length) {
          throw ErrorImport.getErrorFormatValues(
            errorFormatValues,
            `Dominio: ${domainEntity.id} - ${domainEntity.name}`
          )
        }
      }
      return domainEntity
    } catch (error) {
      LogService.error('Error durante la actualización de valores de un dominio.')
      throw error
    }
  }

  hideFeedback (): void {
    this.onSaveDomainFeedback = false
  }
  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importDomainFields' variable
   * @returns object with column values to use on import file action
   */
  domainDetails (domain: any) {
    return {
      id: domain.id,
      name: domain.name
    }
  }

  /**
   * Gets the save client funtion from catalogService to use in import component
   */
  get saveDomain () {
    return catalogsService.saveDomain
  }

  showOpenCloseDomainModal () {
    this.$bvModal.show('open-close-domain-modal')
  }

  hideOpenCloseDomainModal () {
    this.$bvModal.hide('open-close-domain-modal')
  }

  async handleOpenCloseDomain () {
    this.openCloseDomainLoading = true
    try {
      if (!this.selectedItem.closed) {
        const releaseLicensesResponse = await catalogsService.releaseDomainLicenses(this.selectedItem.id)
        this.$bvToast.toast(`Se han liberado ${releaseLicensesResponse.releasedLicenses} ${releaseLicensesResponse.releasedLicenses === 1 ? 'licencia' : 'licencias'}.`, {
          title: 'Licencias liberadas',
          variant: 'success',
          solid: true
        })
      }

      await catalogsService.closeDomain(this.selectedItem.id, !this.selectedItem.closed)
      this.selectedItem.closed = !this.selectedItem.closed
      this.$bvToast.toast(`El dominio se ha ${this.selectedItem.closed ? 'cerrado' : 'abierto'} correctamente.`, {
        title: 'Operación exitosa',
        variant: 'success',
        solid: true
      })
      this.hideOpenCloseDomainModal()
    } catch (error) {
      LogService.error(`Error al intentar ${this.openCloseLabel.toLowerCase()} el dominio: ${ToString(error)}`)
      this.$bvToast.toast(`No se ha podido ${this.openCloseLabel.toLowerCase()} el dominio. Inténtelo nuevamente más tarde.`, {
        title: 'Error',
        variant: 'danger',
        solid: true
      })
    } finally {
      this.openCloseDomainLoading = false
    }
  }

  /* Export data */

  printCsv () {
    let csv = this.columns.map((col) => { return reportsService.formatTextForCsvFile(col.name) }).join(',') + '\n'

    for (let it of this.filteredItems) {
      csv += `${reportsService.formatTextForCsvFile(it.name)},`
      csv += `${reportsService.formatNullableTextForCsvFile(it.id)},`
      csv += `${reportsService.formatTextForCsvFile(it.status)},`
      csv += `${reportsService.formatTextForCsvFile(it.ownerDomain)},`

      if (this.isFinancesRole) {
        csv += `${reportsService.formatTextForCsvFile(it.domainTypeName)},`
        csv += `${reportsService.formatTextForCsvFile(it.productLine)},`
        csv += `${reportsService.formatTextForCsvFile(it.businessSegment)},`
        csv += `${reportsService.formatTextForCsvFile(it.billingCode)},`
      } else if (this.isOperationRole) {
        csv += `${reportsService.formatTextForCsvFile(it.securityLevelName)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.webBMFMinimumRevision)},`
        csv += `${reportsService.formatTextForCsvFile(it.broadcast)},`
        csv += `${reportsService.formatTextForCsvFile(it.usePredictiveDialerOpt as any)},`
        csv += `${reportsService.formatTextForCsvFile(it.limitLicenses as any)},`
        csv += `${reportsService.formatTextForCsvFile(it.mapProvider)},`
        csv += `${reportsService.formatTextForCsvFile(it.fullTextSearchType)},`
        csv += `${reportsService.formatTextForCsvFile(it.trackingMode)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.rateLimitTime)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.rateLimitWS)},`
      }
      csv += `${reportsService.formatNullableTextForCsvFile(it.firebaseUUID)},`
      csv += `${it.creationDateString},`
      csv += `${it.updateDateString}\n`
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `dominios.csv`
    element.click()
  }
}
</script>

<style scoped lang="scss">
.feedback-domain.on-domain-saved
{
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 999;

  &.success .toast-body{
    p {
      margin: 0
    }
    span {
      font-weight: bold;
    }
    .task-id {
      font-size: 1rem;
    }
  }

  // Success
  &.success .toast-header {
    background-color: #c3e6cb;
    color: #155724;
  }
  &.success .toast-body {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
  }
}
</style>
